(function () {
  'use strict';

  if (window.UnifiedPerformance) {
    return;
  }

  const VERSION = '1.0.0';
  const MAX_MEASUREMENTS = 300;

  const measurements = [];
  const activeMeasurements = new Map();

  let sequence = 0;

  function getConfig() {
    return window.UnifiedTrackingConfig || {};
  }

  function isDebugEnabled() {
    return getConfig().debug === true;
  }

  function getNow() {
    if (
      window.performance &&
      typeof window.performance.now === 'function'
    ) {
      return window.performance.now();
    }

    return Date.now();
  }

  function normalizeLabel(label) {
    return String(label || 'unknown')
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9_.-]+/g, '_')
      .replace(/^_+|_+$/g, '');
  }

  function sanitizeMeta(meta) {
    const source = meta || {};
    const sanitized = {};

    Object.keys(source)
      .slice(0, 20)
      .forEach(function (key) {
        const value = source[key];

        if (
          typeof value === 'string' ||
          typeof value === 'number' ||
          typeof value === 'boolean'
        ) {
          sanitized[key] = value;
        }
      });

    return sanitized;
  }

  function start(label, meta) {
    sequence += 1;

    const token = {
      id: sequence,
      label: normalizeLabel(label),
      startedAt: getNow(),
      meta: sanitizeMeta(meta)
    };

    activeMeasurements.set(
      token.id,
      token
    );

    return token.id;
  }

  function end(tokenId, extraMeta) {
    const token = activeMeasurements.get(
      tokenId
    );

    if (!token) {
      return null;
    }

    activeMeasurements.delete(tokenId);

    const endedAt = getNow();

    const duration = Math.max(
      0,
      endedAt - token.startedAt
    );

    const measurement = {
      id: token.id,
      label: token.label,

      duration_ms: Number(
        duration.toFixed(3)
      ),

      recorded_at:
        new Date().toISOString(),

      meta: Object.assign(
        {},
        token.meta,
        sanitizeMeta(extraMeta)
      )
    };

    measurements.push(measurement);

    if (
      measurements.length >
      MAX_MEASUREMENTS
    ) {
      measurements.shift();
    }

    return Object.assign(
      {},
      measurement
    );
  }

  function measure(label, callback, meta) {
    const tokenId = start(
      label,
      meta
    );

    try {
      const result = callback();

      end(tokenId, {
        status: 'success'
      });

      return result;
    } catch (error) {
      end(tokenId, {
        status: 'error',
        error_name:
          error && error.name
            ? error.name
            : 'Error'
      });

      throw error;
    }
  }

  function getMeasurements() {
    return measurements.map(
      function (measurement) {
        return Object.assign(
          {},
          measurement,
          {
            meta: Object.assign(
              {},
              measurement.meta
            )
          }
        );
      }
    );
  }

  function getByLabel(label) {
    const normalizedLabel =
      normalizeLabel(label);

    return getMeasurements().filter(
      function (measurement) {
        return (
          measurement.label ===
          normalizedLabel
        );
      }
    );
  }

  function calculateLabelSummary(
    label,
    items
  ) {
    if (!items.length) {
      return null;
    }

    const durations = items.map(
      function (item) {
        return Number(
          item.duration_ms
        ) || 0;
      }
    );

    const total = durations.reduce(
      function (sum, duration) {
        return sum + duration;
      },
      0
    );

    return {
      label: label,

      count: items.length,

      total_ms: Number(
        total.toFixed(3)
      ),

      average_ms: Number(
        (
          total / items.length
        ).toFixed(3)
      ),

      minimum_ms: Number(
        Math.min.apply(
          Math,
          durations
        ).toFixed(3)
      ),

      maximum_ms: Number(
        Math.max.apply(
          Math,
          durations
        ).toFixed(3)
      )
    };
  }

  function getSummary() {
    const grouped = {};

    measurements.forEach(
      function (measurement) {
        if (!grouped[measurement.label]) {
          grouped[measurement.label] = [];
        }

        grouped[measurement.label].push(
          measurement
        );
      }
    );

    const labels = Object.keys(grouped)
      .map(function (label) {
        return calculateLabelSummary(
          label,
          grouped[label]
        );
      })
      .filter(Boolean);

    const totalDuration = measurements.reduce(
      function (sum, measurement) {
        return (
          sum +
          (
            Number(
              measurement.duration_ms
            ) || 0
          )
        );
      },
      0
    );

    const slowestMeasurement =
      measurements.length
        ? measurements.reduce(
            function (slowest, current) {
              if (
                !slowest ||
                current.duration_ms >
                  slowest.duration_ms
              ) {
                return current;
              }

              return slowest;
            },
            null
          )
        : null;

    return {
      version: VERSION,

      total_measurements:
        measurements.length,

      active_measurements:
        activeMeasurements.size,

      total_duration_ms: Number(
        totalDuration.toFixed(3)
      ),

      labels: labels,

      slowest_measurement:
        slowestMeasurement
          ? Object.assign(
              {},
              slowestMeasurement
            )
          : null
    };
  }

  function printSummary() {
    const summary = getSummary();

    if (!summary.labels.length) {
      console.info(
        '[Unified Performance] No measurements recorded.'
      );

      return summary;
    }

    console.table(
      summary.labels.map(
        function (item) {
          return {
            label: item.label,
            count: item.count,
            average_ms:
              item.average_ms,
            minimum_ms:
              item.minimum_ms,
            maximum_ms:
              item.maximum_ms,
            total_ms:
              item.total_ms
          };
        }
      )
    );

    console.info(
      '[Unified Performance] Summary',
      {
        totalMeasurements:
          summary.total_measurements,

        totalDurationMs:
          summary.total_duration_ms,

        activeMeasurements:
          summary.active_measurements,

        slowestMeasurement:
          summary.slowest_measurement
      }
    );

    return summary;
  }

  function clear() {
    measurements.length = 0;
    activeMeasurements.clear();

    console.info(
      '[Unified Performance] Measurements cleared.'
    );

    return true;
  }

  window.UnifiedPerformance = {
    version: VERSION,

    start: start,

    end: end,

    measure: measure,

    getMeasurements:
      getMeasurements,

    getByLabel: getByLabel,

    getSummary: getSummary,

    printSummary: printSummary,

    clear: clear
  };

  if (isDebugEnabled()) {
    console.info(
      '[Unified Performance] Performance Monitor initialized',
      {
        version: VERSION,
        maximumMeasurements:
          MAX_MEASUREMENTS
      }
    );
  }
})();